import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../api/config';

// Derive the public URL for a stored application image
const getImageUrl = (customer) => {
  if (customer?.image_path) {
    return `${API_BASE_URL}/image_serve.php?id=${customer.application_id}`;
  }
  return null;
};

/* ── Section definitions ────────────────────────────────────────── */
const SECTIONS = [
  {
    key: 'personal',
    title: 'Personal Information',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
    ),
    color: 'text-blue-600 bg-blue-50 border-blue-200',
    accent: 'border-l-blue-500',
    fields: [
      { key: 'client_name',     label: 'Client Name' },
      { key: 'father_name',     label: 'Father Name' },
      { key: 'mother_name',     label: 'Mother Name' },
      { key: 'date_of_birth',   label: 'Date of Birth',   type: 'date' },
      { key: 'email',           label: 'Email',            type: 'email' },
      { key: 'contact_no',      label: 'Contact No' },
      { key: 'marital_status',  label: 'Marital Status',
        type: 'select', options: ['Single', 'Married', 'Divorced', 'Widowed'] },
      { key: 'dependents',      label: 'Dependents',       type: 'number' },
      { key: 'spouse_name',     label: 'Spouse Name' },
      { key: 'spouse_dob',      label: 'Spouse DOB',       type: 'date' },
      { key: 'date',            label: 'Application Date', type: 'date', readOnly: true },
    ],
  },
  {
    key: 'identity',
    title: 'Identity Information',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2" />
      </svg>
    ),
    color: 'text-violet-600 bg-violet-50 border-violet-200',
    accent: 'border-l-violet-500',
    fields: [
      { key: 'pan_no',    label: 'PAN No' },
      { key: 'aadhar_no', label: 'Aadhar No' },
    ],
  },
  {
    key: 'loan_details',
    title: 'Loan Request Information',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
    color: 'text-rose-600 bg-rose-50 border-rose-200',
    accent: 'border-l-rose-500',
    fields: [
      { key: 'dsa_name',     label: 'DSA Name' },
      { key: 'product_type', label: 'Product Type', type: 'select', options: ['Personal Loan', 'Business Loan', 'Home Loan', 'Auto Loan', 'Mortgage', 'Education Loan', 'Gold Loan'] },
      { key: 'loan_od',      label: 'Loan OD', type: 'select', options: ['LOAN', 'OD', 'CC'] },
    ],
  },
  {
    key: 'address',
    title: 'Address Information',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    color: 'text-emerald-600 bg-emerald-50 border-emerald-200',
    accent: 'border-l-emerald-500',
    fields: [
      { key: 'address_door_no',      label: 'Door No' },
      { key: 'current_address',      label: 'Current Address',      type: 'textarea' },
      { key: 'current_landmark',     label: 'Current Landmark' },
      { key: 'residential_address',  label: 'Residential Address',  type: 'textarea' },
      { key: 'residential_landmark', label: 'Residential Landmark' },
      { key: 'residence_ownership',  label: 'Residence Ownership',
        type: 'select', options: ['OWN', 'RENTED'] },
      { key: 'years_in_residence',   label: 'Years in Residence',   type: 'number' },
      { key: 'staying_in_chennai',   label: 'Staying in Chennai',
        type: 'select', options: ['Yes', 'No'] },
      { key: 'permanent_address',    label: 'Permanent Address',    type: 'textarea' },
      { key: 'permanent_landmark',   label: 'Permanent Landmark' },
    ],
  },
  {
    key: 'employment',
    title: 'Employment Information',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
    color: 'text-orange-600 bg-orange-50 border-orange-200',
    accent: 'border-l-orange-500',
    fields: [
      { key: 'customer_type',           label: 'Customer Type',
        type: 'select', options: ['SALARIED', 'SELF_EMPLOYED'] },
      { key: 'company_name',            label: 'Company Name' },
      { key: 'office_name',             label: 'Office Name' },
      { key: 'office_address',          label: 'Office Address',       type: 'textarea' },
      { key: 'office_landmark',         label: 'Office Landmark' },
      { key: 'designation',             label: 'Designation' },
      { key: 'official_mail',           label: 'Official Email',        type: 'email' },
      { key: 'office_landline',         label: 'Office Landline' },
      { key: 'current_job_experience',  label: 'Current Job Exp.' },
      { key: 'total_work_experience',   label: 'Total Work Exp.' },
      { key: 'education_qualification', label: 'Education' },
      { key: 'nature_of_business',      label: 'Nature of Business' },
    ],
  },
  {
    key: 'financial',
    title: 'Financial Information',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    color: 'text-teal-600 bg-teal-50 border-teal-200',
    accent: 'border-l-teal-500',
    fields: [
      { key: 'gross_income',               label: 'Gross Income',          type: 'number' },
      { key: 'net_income',                 label: 'Net Income',            type: 'number' },
      { key: 'required_loan_amount',       label: 'Required Loan Amount',  type: 'number' },
      { key: 'tenure',                     label: 'Tenure' },
      { key: 'bank_name',                  label: 'Bank Name' },
      { key: 'login',                      label: 'Login' },
      { key: 'live_loan',                  label: 'Live Loan' },
      { key: 'current_business_experience',label: 'Business Experience' },
    ],
  },
];

function statusBadge(status) {
  const s = String(status).toUpperCase();
  if (s === 'VERIFIED')  return { cls: 'bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200', dot: 'bg-emerald-500' };
  if (s === 'EXTRACTED') return { cls: 'bg-blue-100 text-blue-700 ring-1 ring-blue-200',        dot: 'bg-blue-500' };
  if (s === 'DRAFT')     return { cls: 'bg-slate-100 text-slate-600 ring-1 ring-slate-200',      dot: 'bg-slate-400' };
  if (s === 'REJECTED')  return { cls: 'bg-red-100 text-red-700 ring-1 ring-red-200',            dot: 'bg-red-500' };
  return                          { cls: 'bg-slate-100 text-slate-600 ring-1 ring-slate-200',    dot: 'bg-slate-400' };
}

function displayVal(fieldDef, value) {
  if (value === null || value === undefined || value === '') {
    return <span className="text-slate-300 italic text-xs">Not provided</span>;
  }
  if (fieldDef.key === 'staying_in_chennai') {
    return <span>{value === 1 || value === '1' || value === true ? 'Yes' : 'No'}</span>;
  }
  if (fieldDef.type === 'number' && (fieldDef.key.includes('income') || fieldDef.key.includes('loan'))) {
    return (
      <span className="font-semibold text-slate-800">
        ₹ {Number(value).toLocaleString('en-IN')}
      </span>
    );
  }
  return <span>{String(value)}</span>;
}

/* ── Icons ──────────────────────────────────────────────────────── */
const PencilIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M15.232 5.232l3.536 3.536M9 13l6.586-6.586a2 2 0 112.828 2.828L11.828 15.828a2 2 0 01-1.414.586H7v-3a2 2 0 01.586-1.414z" />
  </svg>
);

const CheckIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

const XIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

/* ── Section card ───────────────────────────────────────────────── */
const SectionCard = ({ section, customer, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const startEdit = () => {
    const initial = {};
    section.fields.forEach(({ key }) => {
      initial[key] = customer[key] !== undefined ? customer[key] : '';
    });
    setDraft(initial);
    setSaveError(null);
    setEditing(true);
  };

  const cancel = () => { setEditing(false); setSaveError(null); };

  const save = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await onSave(section.key, draft);
      setEditing(false);
    } catch (err) {
      setSaveError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const set = (key, val) => setDraft((d) => ({ ...d, [key]: val }));

  const visibleFields = section.fields.filter(
    (f) => editing || customer[f.key] !== undefined
  );

  if (visibleFields.length === 0) return null;

  return (
    <div className={`bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden h-full border-l-4 ${section.accent}`}>
      {/* Section header */}
      <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between bg-slate-50">
        <div className="flex items-center gap-2.5">
          <span className={`p-1.5 rounded-lg border ${section.color}`}>
            {section.icon}
          </span>
          <h3 className="font-semibold text-slate-700 text-sm">{section.title}</h3>
        </div>
        {!editing ? (
          <button
            onClick={startEdit}
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-slate-500 hover:text-accent-600 hover:bg-accent-50 border border-slate-200 hover:border-accent-300 rounded-lg transition-all"
          >
            <PencilIcon /> Edit
          </button>
        ) : (
          <div className="flex items-center gap-2">
            {saveError && <span className="text-xs text-red-500 max-w-[120px] truncate">{saveError}</span>}
            <button
              onClick={cancel}
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100 border border-slate-200 rounded-lg transition-all"
            >
              <XIcon /> Cancel
            </button>
            <button
              onClick={save}
              disabled={saving}
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-white bg-accent-500 hover:bg-accent-600 rounded-lg transition-all disabled:opacity-50 shadow-sm"
            >
              <CheckIcon /> {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        )}
      </div>

      {/* Fields */}
      <div className="divide-y divide-slate-50">
        {visibleFields.map((fieldDef, idx) => {
          const value = customer[fieldDef.key];
          const draftVal = draft[fieldDef.key] ?? '';
          const isReadOnly = fieldDef.readOnly;

          return (
            <div
              key={fieldDef.key}
              className={`flex items-start justify-between gap-3 px-5 py-2.5 ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}
            >
              <span className="text-xs font-medium text-slate-800 w-36 flex-shrink-0 pt-0.5 leading-relaxed">
                {fieldDef.label}
              </span>
              <div className="flex-1 text-right min-w-0">
                {editing && !isReadOnly ? (
                  fieldDef.type === 'select' ? (
                    <select
                      value={draftVal}
                      onChange={(e) => set(fieldDef.key, e.target.value)}
                      className="border border-slate-300 rounded-lg px-2.5 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-accent-400 focus:border-accent-400 w-full text-left bg-white transition-shadow"
                    >
                      <option value="">— Select —</option>
                      {fieldDef.options.map((o) => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                    </select>
                  ) : fieldDef.type === 'textarea' ? (
                    <textarea
                      value={draftVal}
                      onChange={(e) => set(fieldDef.key, e.target.value)}
                      rows={2}
                      className="border border-slate-300 rounded-lg px-2.5 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-accent-400 focus:border-accent-400 w-full resize-none text-left bg-white transition-shadow"
                    />
                  ) : (
                    <input
                      type={fieldDef.type || 'text'}
                      value={draftVal}
                      onChange={(e) => set(fieldDef.key, e.target.value)}
                      className="border border-slate-300 rounded-lg px-2.5 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-accent-400 focus:border-accent-400 w-full text-left bg-white transition-shadow"
                    />
                  )
                ) : (
                  <span className="text-sm text-slate-700 break-words">{displayVal(fieldDef, value)}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ── Generic References section ─────────────────────────────────── */
const REF_FIELDS = [
  { key: 'name',           label: 'Name' },
  { key: 'phone',          label: 'Phone' },
  { key: 'address',        label: 'Address',   type: 'textarea' },
  { key: 'relation',       label: 'Relation' },
  { key: 'reference_type', label: 'Type' },
];

const APP_REF_FIELDS = [
  { key: 'name',           label: 'Name' },
  { key: 'phone',          label: 'Phone' },
  { key: 'mobile',         label: 'Mobile' },
  { key: 'address',        label: 'Address',   type: 'textarea' },
  { key: 'relation',       label: 'Relation' },
  { key: 'reference_type', label: 'Type' },
  { key: 'years_known',    label: 'Years Known' },
];

const ReferencesSection = ({
  title = 'References',
  references,
  onRefSave,
  fields = REF_FIELDS,
  borderColor = 'border-l-indigo-500',
  iconColor   = 'text-indigo-600 bg-indigo-50 border-indigo-200',
  badgeColor  = 'bg-indigo-100 text-indigo-600',
  labelColor  = 'text-indigo-600',
  alwaysShow  = false,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState([]);
  const [saving, setSaving]   = useState(false);
  const [saveError, setSaveError] = useState(null);

  const hasData = Array.isArray(references) && references.length > 0;
  if (!hasData && !alwaysShow) return null;

  /* Derive which fields actually have data (for read view) */
  const visibleFields = (ref) =>
    fields.filter((f) => ref[f.key] !== undefined && ref[f.key] !== null && ref[f.key] !== '');

  const startEdit = () => {
    setDraft(references.map((r) => ({ ...r })));
    setSaveError(null);
    setEditing(true);
  };

  const cancel = () => { setEditing(false); setSaveError(null); };

  const save = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await onRefSave(draft);
      setEditing(false);
    } catch (err) {
      setSaveError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const setField = (idx, key, val) => {
    setDraft((d) => d.map((r, i) => i === idx ? { ...r, [key]: val } : r));
  };

  return (
    <div className={`bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden h-full border-l-4 ${borderColor}`}>
      <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between bg-slate-50">
        <div className="flex items-center gap-2.5">
          <span className={`p-1.5 rounded-lg border ${iconColor}`}>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </span>
          <h3 className="font-semibold text-slate-700 text-sm">{title}</h3>
          <span className="text-xs text-slate-400 font-medium">{hasData ? `${references.length} record${references.length !== 1 ? 's' : ''}` : 'No records'}</span>
        </div>
        {!editing ? (
          <button
            onClick={startEdit}
            disabled={!hasData}
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-slate-500 hover:text-accent-600 hover:bg-accent-50 border border-slate-200 hover:border-accent-300 rounded-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <PencilIcon /> Edit
          </button>
        ) : (
          <div className="flex items-center gap-2">
            {saveError && <span className="text-xs text-red-500 max-w-[120px] truncate">{saveError}</span>}
            <button onClick={cancel} className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100 border border-slate-200 rounded-lg transition-all">
              <XIcon /> Cancel
            </button>
            <button onClick={save} disabled={saving} className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-white bg-accent-500 hover:bg-accent-600 rounded-lg transition-all disabled:opacity-50 shadow-sm">
              <CheckIcon /> {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        )}
      </div>

      {!hasData && (
        <div className="px-5 py-8 text-center text-slate-400 text-sm italic">No records found.</div>
      )}
      {hasData && (
        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {(editing ? draft : references).map((ref, idx) => {
            const rowFields = editing ? fields : (visibleFields(ref).length > 0 ? visibleFields(ref) : fields);
            return (
              <div key={idx} className="rounded-xl border border-slate-100 overflow-hidden">
                {/* Column header */}
                <div className={`flex items-center gap-2 px-4 py-2.5 ${badgeColor} bg-opacity-20`}
                  style={{ background: 'inherit' }}>
                  <span className={`w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center flex-shrink-0 ${badgeColor}`}>
                    {ref.reference_number || idx + 1}
                  </span>
                  <p className={`text-xs font-semibold uppercase tracking-wide ${labelColor}`}>
                    Reference {ref.reference_number || idx + 1}
                    {ref.reference_type ? ` · ${ref.reference_type}` : ''}
                    {ref.relation ? ` · ${ref.relation}` : ''}
                  </p>
                </div>
                {/* Fields */}
                <div className="divide-y divide-slate-50">
                  {rowFields.map(({ key, label, type }, fi) => (
                    <div key={key} className={`flex items-start justify-between gap-3 px-4 py-2 ${fi % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}>
                      <span className="text-xs font-medium text-slate-800 w-24 flex-shrink-0 pt-0.5">{label}</span>
                      <div className="flex-1 text-right min-w-0">
                        {editing ? (
                          type === 'textarea' ? (
                            <textarea
                              value={draft[idx]?.[key] || ''}
                              onChange={(e) => setField(idx, key, e.target.value)}
                              rows={2}
                              className="border border-slate-300 rounded-lg px-2.5 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-accent-400 focus:border-accent-400 w-full resize-none text-left bg-white"
                            />
                          ) : (
                            <input
                              type="text"
                              value={draft[idx]?.[key] || ''}
                              onChange={(e) => setField(idx, key, e.target.value)}
                              className="border border-slate-300 rounded-lg px-2.5 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-accent-400 focus:border-accent-400 w-full text-left bg-white"
                            />
                          )
                        ) : (
                          <span className="text-sm text-slate-700 break-words">
                            {ref[key] || <span className="text-slate-300 italic text-xs">Not provided</span>}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ── Extract flat reference fields into array ───────────────────── */
function extractFlatReferences(customer, count = 5) {
  const refs = [];
  for (let i = 1; i <= count; i++) {
    const name = customer[`reference_${i}_name`];
    if (!name) continue;
    refs.push({
      reference_number: i,
      name,
      phone:          customer[`reference_${i}_phone`]   || '',
      address:        customer[`reference_${i}_address`] || '',
      relation:       customer[`reference_${i}_relation`]|| '',
      reference_type: customer[`reference_${i}_type`]    || '',
    });
  }
  return refs;
}

/* ── Main page ──────────────────────────────────────────────────── */
const CustomerDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightbox, setLightbox] = useState(false);
  // imgError: true when image_serve returns 404 (file deleted from disk)
  const [imgError, setImgError] = useState(false);

  useEffect(() => { fetchCustomer(); }, [id]);

  const fetchCustomer = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/applications.php?action=get&id=${id}`);
      const data = await response.json();
      if (data.success === false) {
        throw new Error(data.error || data.message || 'Failed to load customer details.');
      }
      setCustomer(data.data || data);
    } catch (err) {
      setError(err.message || 'Failed to load customer details.');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (sectionKey, draft) => {
    const response = await fetch(`${API_BASE_URL}/applications.php?action=update&id=${id}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ section: sectionKey, data: draft }),
    });
    const result = await response.json();
    if (result.success === false) throw new Error(result.message || 'Update failed');
    setCustomer((prev) => ({ ...prev, ...draft }));
  };

  const handleRefSave = async (draft) => {
    const response = await fetch(`${API_BASE_URL}/applications.php?action=update_references&id=${id}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ references: draft }),
    });
    const result = await response.json();
    if (result.success === false) throw new Error(result.message || 'Update failed');
    setCustomer((prev) => ({ ...prev, references: draft }));
  };

  const handleAppRefSave = async (draft) => {
    const response = await fetch(`${API_BASE_URL}/applications.php?action=update_application_references&id=${id}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ application_references: draft }),
    });
    const result = await response.json();
    if (result.success === false) throw new Error(result.message || 'Update failed');
    setCustomer((prev) => ({ ...prev, application_references: draft }));
  };

  const handleDownload = () => {
    const name = customer?.client_name || customer?.applicant_name || `customer_${customer?.application_id}`;
    const safeName = name.replace(/\s+/g, '_');
    const imageUrl = getImageUrl(customer);
    if (imageUrl && !imgError) {
      // Path-based: open in new tab — browser will prompt save
      const link = document.createElement('a');
      link.href = imageUrl;
      link.download = `${safeName}.jpg`;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.click();
    }
    // Legacy base64 path intentionally removed — old records should be
    // retrieved via image_serve once DB cleanup is done manually.
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-32 gap-4 text-slate-400">
        <div className="relative w-12 h-12">
          <svg className="animate-spin w-12 h-12 text-accent-500" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-20" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
            <path className="opacity-80" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
          </svg>
        </div>
        <span className="text-sm font-medium text-slate-500">Loading customer details…</span>
      </div>
    );
  }

  if (error || !customer) {
    return (
      <div className="flex flex-col items-center justify-center py-32 gap-4">
        <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center">
          <svg className="w-7 h-7 text-red-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <p className="text-slate-700 font-medium">{error || 'Customer not found.'}</p>
        <button
          onClick={() => navigate('/dashboard/customers')}
          className="flex items-center gap-2 px-4 py-2 bg-accent-500 text-white rounded-xl hover:bg-accent-600 transition-colors text-sm font-medium shadow-sm"
        >
          Back to Customers
        </button>
      </div>
    );
  }

  const customerName = customer.client_name || customer.applicant_name || customer.full_name || `Customer #${id}`;
  const references = Array.isArray(customer.references) && customer.references.length > 0
    ? customer.references
    : extractFlatReferences(customer);
  const appReferences = Array.isArray(customer.application_references) ? customer.application_references : [];
  const badge = customer.status ? statusBadge(customer.status) : null;

  return (
    <>
      <div className="space-y-5 max-w-6xl mx-auto pb-8">

        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <button
            onClick={() => navigate('/dashboard/customers')}
            className="hover:text-accent-600 transition-colors flex items-center gap-1.5 font-medium"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Customers
          </button>
          <svg className="w-3 h-3 text-slate-300" fill="currentColor" viewBox="0 0 6 10">
            <path d="M1 1l4 4-4 4" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span className="text-slate-800 font-semibold truncate">{customerName}</span>
        </div>

        {/* Hero card */}
        <div className="rounded-2xl border border-amber-200 shadow-md overflow-hidden" style={{ background: '#fff' }}>

          {/* ── Gold banner — avatar + name live INSIDE it ── */}
          <div className="relative px-6 py-5 flex items-center gap-5"
            style={{ background: 'linear-gradient(120deg,rgba(133, 113, 15, 1) 0%, rgba(186, 165, 28, 1) 35%, rgba(217, 201, 59, 1) 70%, #c9a84c 100%)' }}>

            {/* Decorative blobs */}
            <div className="absolute -top-8 -right-8 w-44 h-44 rounded-full pointer-events-none" style={{ background: 'rgba(255,255,255,0.05)' }} />
            <div className="absolute bottom-0 left-1/3 w-32 h-32 rounded-full pointer-events-none" style={{ background: 'rgba(255,255,255,0.04)' }} />

            {/* Avatar */}
            <div className="flex-shrink-0 relative z-10">
              {getImageUrl(customer) && !imgError ? (
                <button
                  onClick={() => setLightbox(true)}
                  className="w-16 h-16 rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow"
                  style={{ border: '2px solid rgba(255,255,255,0.5)' }}
                  title="Click to view full image"
                >
                  <img
                    src={getImageUrl(customer)}
                    alt={customerName}
                    className="w-full h-full object-cover"
                    onError={() => setImgError(true)}
                  />
                </button>
              ) : (
                <div
                  className="w-16 h-16 rounded-xl shadow-lg flex items-center justify-center text-2xl font-bold text-white"
                  style={{ background: 'rgba(255,255,255,0.15)', border: '2px solid rgba(255,255,255,0.3)' }}
                  title={imgError ? 'Image file deleted from server' : 'No image uploaded'}
                >
                  {customerName.charAt(0).toUpperCase()}
                </div>
              )}
            </div>

            {/* Name + contact + badges — all on banner */}
            <div className="flex-1 min-w-0 relative z-10">
              <h2 className="text-lg font-bold leading-snug text-white">{customerName}</h2>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-0.5 mt-0.5">
                {customer.email    && <span className="text-xs" style={{ color: 'rgba(255,255,255,0.75)' }}>{customer.email}</span>}
                {customer.contact_no && <span className="text-xs" style={{ color: 'rgba(255,255,255,0.75)' }}>{customer.contact_no}</span>}
              </div>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                {badge && (
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold ${badge.cls}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${badge.dot}`} />
                    {customer.status}
                  </span>
                )}
                {customer.customer_type && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold"
                    style={{ background: 'rgba(255,255,255,0.15)', color: '#fde68a', border: '1px solid rgba(255,255,255,0.25)' }}>
                    {customer.customer_type}
                  </span>
                )}
              </div>
            </div>

            {/* App # + date — top-right of banner */}
            <div className="flex-shrink-0 flex flex-col items-end gap-1 relative z-10">
              {customer.application_number && (
                <span className="text-xs font-mono font-semibold px-3 py-1 rounded-full"
                  style={{ background: 'rgba(255,255,255,0.15)', color: '#fde68a', border: '1px solid rgba(255,255,255,0.2)' }}>
                  App# {customer.application_number}
                </span>
              )}
              {customer.created_at && (
                <span className="text-xs" style={{ color: 'rgba(253,230,138,0.7)' }}>
                  {new Date(customer.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                </span>
              )}
            </div>
          </div>

          {/* ── White area: stats + actions ── */}
          <div className="px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-t border-amber-100">

            {/* Stat chips */}
            <div className="flex flex-wrap gap-2">
              {customer.required_loan_amount && (
                <div className="flex flex-col px-4 py-2 rounded-xl border border-amber-200 bg-amber-50">
                  <span className="text-[10px] uppercase tracking-widest text-amber-600 font-semibold">Loan Amount</span>
                  <span className="text-sm font-bold text-slate-800 mt-0.5">
                    ₹ {Number(customer.required_loan_amount).toLocaleString('en-IN')}
                  </span>
                </div>
              )}
              {customer.tenure && (
                <div className="flex flex-col px-4 py-2 rounded-xl border border-slate-200 bg-slate-50">
                  <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Tenure</span>
                  <span className="text-sm font-bold text-slate-800 mt-0.5">{customer.tenure}</span>
                </div>
              )}
              {customer.bank_name && (
                <div className="flex flex-col px-4 py-2 rounded-xl border border-slate-200 bg-slate-50">
                  <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Bank</span>
                  <span className="text-sm font-bold text-slate-800 mt-0.5">{customer.bank_name}</span>
                </div>
              )}
              {customer.login && (
                <div className="flex flex-col px-4 py-2 rounded-xl border border-slate-200 bg-slate-50">
                  <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Login</span>
                  <span className="text-sm font-bold text-slate-800 mt-0.5 max-w-[140px] truncate">{customer.login}</span>
                </div>
              )}
            </div>

            {/* Photo action buttons */}
            {getImageUrl(customer) && !imgError && (
              <div className="flex gap-2 flex-shrink-0">
                <button
                  onClick={() => setLightbox(true)}
                  className="flex items-center gap-1.5 px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-lg text-xs font-medium transition-colors border border-slate-200"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                  View Photo
                </button>
                <button
                  onClick={handleDownload}
                  className="flex items-center gap-1.5 px-3 py-2 text-white rounded-lg text-xs font-medium transition-opacity hover:opacity-90 shadow-sm"
                  style={{ background: 'linear-gradient(135deg, #6b4700, #B8942B)' }}
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1M16 12l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download
                </button>
              </div>
            )}
            {imgError && (
              <div className="flex-shrink-0">
                <span className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5 font-medium">
                  ⚠ Image file deleted from server
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Section cards — Row 1: Personal | Employment */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-stretch">
          <SectionCard section={SECTIONS[0]} customer={customer} onSave={handleSave} />
          <SectionCard section={SECTIONS[4]} customer={customer} onSave={handleSave} />
        </div>

        {/* Row 2: Financial | Address */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-stretch">
          <SectionCard section={SECTIONS[5]} customer={customer} onSave={handleSave} />
          <SectionCard section={SECTIONS[3]} customer={customer} onSave={handleSave} />
        </div>

     

        {/* Row 3: References — full width */}
        <ReferencesSection references={references} onRefSave={handleRefSave} />

   {/* Row 4: Identity & Loan Details */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">
          <SectionCard section={SECTIONS[1]} customer={customer} onSave={handleSave} />
          <SectionCard section={SECTIONS[2]} customer={customer} onSave={handleSave} />
        </div>
        {/* Row 4: Application References (always shown) */}
        {/* <ReferencesSection
          title="Application References"
          references={appReferences}
          onRefSave={handleAppRefSave}
          fields={APP_REF_FIELDS}
          borderColor="border-l-rose-500"
          iconColor="text-rose-600 bg-rose-50 border-rose-200"
          badgeColor="bg-rose-100 text-rose-600"
          labelColor="text-rose-600"
          alwaysShow
        /> */}

        {/* Back button */}
        <div>
          <button
            onClick={() => navigate('/dashboard/customers')}
            className="flex items-center gap-2 px-4 py-2.5 border border-slate-200 rounded-xl text-sm text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-all font-medium"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Back to Customers
          </button>
        </div>
      </div>

      {/* Lightbox */}
      {lightbox && getImageUrl(customer) && !imgError && (
        <div
          className="fixed inset-0 flex items-center justify-center z-50 p-6"
          style={{ background: 'rgba(0,0,0,0.88)', backdropFilter: 'blur(4px)' }}
          onClick={() => setLightbox(false)}
        >
          <div className="relative max-w-xl w-full" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setLightbox(false)}
              className="absolute -top-12 right-0 w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
              aria-label="Close"
            >
              <XIcon />
            </button>
            <img
              src={getImageUrl(customer)}
              alt={customerName}
              className="w-full rounded-2xl shadow-2xl object-contain max-h-[75vh]"
              onError={() => { setImgError(true); setLightbox(false); }}
            />
            <p className="text-center text-white/60 text-sm mt-3 font-medium">{customerName}</p>
            <div className="mt-3 flex justify-center">
              <button
                onClick={handleDownload}
                className="flex items-center gap-2 px-5 py-2.5 bg-white text-slate-800 rounded-xl text-sm font-medium hover:bg-slate-100 transition-colors shadow-lg"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1M16 12l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download Image
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CustomerDetailPage;
